import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, MessageSquare, Plus, Send, Wrench } from 'lucide-react';
import {
  faultStatuses,
  threadCreateSchema,
  todayIso,
  type FaultStatus,
  type Thread,
  type ThreadKind,
  type ThreadSummary,
} from '@vuokramani/shared';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  EmptyState,
  SelectField,
  Splash,
  TextArea,
  TextField,
} from '../../components/ui.tsx';
import { errorKey } from '../../lib/api.ts';
import {
  useCreateThread,
  useMarkRead,
  useSendMessage,
  useSetFaultStatus,
  useThread,
  useThreads,
  type MessagesAs,
} from '../../lib/messages.ts';
import { propertyTitle, useProperties } from '../../lib/properties.ts';
import { activeLease, useLeases } from '../../lib/tenancy.ts';
import s from './messages.module.css';

const TIME_ZONE = 'Europe/Helsinki';

/** Tänään kellonaika, muuten päivämäärä. */
function useShortWhen() {
  const { t, i18n } = useTranslation();
  const time = new Intl.DateTimeFormat(i18n.language, { timeStyle: 'short', timeZone: TIME_ZONE });
  const date = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeZone: TIME_ZONE });
  const day = new Intl.DateTimeFormat('sv-SE', { timeZone: TIME_ZONE });
  return (iso: string) => {
    const d = new Date(iso);
    return day.format(d) === todayIso()
      ? t('messages.todayAt', { time: time.format(d) })
      : date.format(d);
  };
}

function useDateTime() {
  const { i18n } = useTranslation();
  const fmt = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: TIME_ZONE,
  });
  return (iso: string) => fmt.format(new Date(iso));
}

export function FaultStatusChip({ status }: { status: FaultStatus }) {
  const { t } = useTranslation();
  return <span className={`${s.chip} ${s[`chip_${status}`]}`}>{t(`faultStatus.${status}`)}</span>;
}

interface MessagesViewProps {
  as: MessagesAs;
  /** Polku, jonka perään ketjun id tulee, esim. /vuokranantaja/viestit */
  basePath: string;
  /** Rajaa yhteen asuntoon (asunnon välilehti, vuokralainen). */
  propertyId?: string;
  /** Näytetäänkö ketjun asunto listassa (vuokranantajan kaikki viestit). */
  showProperty?: boolean;
  /** Voiko aloittaa uuden ketjun (asunnon asetus vuokralaiselle). */
  canCreate?: boolean;
}

/**
 * Viestit: ketjulista vasemmalla, avattu ketju oikealla. Puhelimessa näkyy kerrallaan joko
 * lista tai ketju. Ketju kuuluu vuokrasuhteelle, joten sen näkevät kaikki vuokralaiset.
 */
export function MessagesView({
  as,
  basePath,
  propertyId,
  showProperty,
  canCreate = true,
}: MessagesViewProps) {
  const { t } = useTranslation();
  const { threadId } = useParams();
  const navigate = useNavigate();
  const { data: threads, isLoading } = useThreads(as, propertyId);
  const [creating, setCreating] = useState(false);

  if (isLoading) return <Splash />;

  return (
    <>
      <div className={s.toolbar}>
        <p className={s.lead}>{canCreate ? t('messages.lead') : t('messages.disabled')}</p>
        {canCreate && (
          <Button variant="primary" onClick={() => setCreating(true)}>
            <Plus size={16} aria-hidden="true" />
            {t('messages.new')}
          </Button>
        )}
      </div>

      {threads && threads.length === 0 ? (
        <EmptyState
          icon={<MessageSquare size={22} />}
          title={t('messages.empty')}
          text={t('messages.emptyText')}
        />
      ) : (
        <div className={`${s.layout} ${threadId ? s.hasThread : s.noThread}`}>
          <nav className={s.listCol} aria-label={t('messages.listLabel')}>
            <ul className={s.list}>
              {threads?.map((th) => (
                <ThreadRow
                  key={th.id}
                  thread={th}
                  to={`${basePath}/${th.id}`}
                  active={th.id === threadId}
                  showProperty={showProperty}
                />
              ))}
            </ul>
          </nav>
          <section className={s.threadCol}>
            {threadId ? (
              <ThreadView key={threadId} id={threadId} backTo={basePath} />
            ) : (
              <div className={s.placeholder}>{t('messages.choose')}</div>
            )}
          </section>
        </div>
      )}

      {creating && (
        <NewThreadDialog
          as={as}
          propertyId={propertyId}
          onClose={() => setCreating(false)}
          onCreated={(th) => {
            setCreating(false);
            void navigate(`${basePath}/${th.id}`);
          }}
        />
      )}
    </>
  );
}

function ThreadRow({
  thread: th,
  to,
  active,
  showProperty,
}: {
  thread: ThreadSummary;
  to: string;
  active: boolean;
  showProperty?: boolean;
}) {
  const { t } = useTranslation();
  const when = useShortWhen();
  const Icon = th.kind === 'fault' ? Wrench : MessageSquare;
  const preview = th.lastMessage
    ? `${th.lastMessage.byMe ? `${t('messages.you')}: ` : ''}${th.lastMessage.body}`
    : '';
  return (
    <li>
      <Link
        to={to}
        className={`${s.row} ${active ? s.rowActive : ''} ${th.unread ? s.rowUnread : ''}`}
        aria-current={active ? 'page' : undefined}
      >
        <span
          className={`${s.rowIcon} ${th.kind === 'fault' ? s.rowIconFault : ''}`}
          aria-hidden="true"
        >
          <Icon size={17} />
        </span>
        <span className={s.rowMain}>
          <span className={s.rowTop}>
            <span className={s.rowSubject}>{th.subject}</span>
            <span className={s.rowWhen}>{when(th.lastMessageAt)}</span>
          </span>
          {showProperty && <span className={s.rowProperty}>{th.propertyTitle}</span>}
          <span className={s.rowPreview}>{preview}</span>
          {th.status && (
            <span>
              <FaultStatusChip status={th.status} />
            </span>
          )}
        </span>
        {th.unread && <span className={s.dot} role="img" aria-label={t('messages.unread')} />}
      </Link>
    </li>
  );
}

function ThreadView({ id, backTo }: { id: string; backTo: string }) {
  const { t } = useTranslation();
  const dateTime = useDateTime();
  const { data: thread, isLoading, isError } = useThread(id);
  const markRead = useMarkRead();
  const bottom = useRef<HTMLDivElement>(null);

  // Avaus kuittaa ketjun luetuksi; samoin uusi viesti, joka tulee ketjun ollessa auki.
  const unread = thread?.unread;
  useEffect(() => {
    if (unread) markRead.mutate(id);
  }, [id, unread]);

  const count = thread?.messages.length;
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'nearest' });
  }, [count]);

  if (isLoading) return <Splash />;
  if (isError || !thread) return <p className={s.placeholder}>{t('messages.notFound')}</p>;

  return (
    <div className={s.thread}>
      <header className={s.threadHead}>
        <Link to={backTo} className={s.back}>
          <ArrowLeft size={16} aria-hidden="true" />
          {t('messages.back')}
        </Link>
        <div className={s.threadTitleRow}>
          <h2 className={s.threadTitle}>{thread.subject}</h2>
          {thread.status &&
            (thread.canSetStatus ? (
              <StatusSelect thread={thread} />
            ) : (
              <FaultStatusChip status={thread.status} />
            ))}
        </div>
        <p className={s.threadMeta}>
          {thread.kind === 'fault' ? t('messages.faultIn') : t('messages.threadIn')}{' '}
          {thread.propertyTitle}
        </p>
      </header>

      <ol className={s.messages}>
        {thread.messages.map((m) => (
          <li key={m.id} className={`${s.message} ${m.byMe ? s.mine : ''}`}>
            <div className={s.bubble}>{m.body}</div>
            <div className={s.messageMeta}>
              {m.byMe ? t('messages.you') : (m.authorName ?? t('messages.unknown'))},{' '}
              {dateTime(m.createdAt)}
            </div>
          </li>
        ))}
      </ol>
      <div ref={bottom} />

      {thread.canReply ? (
        <Composer threadId={thread.id} />
      ) : (
        <p className={s.threadMeta}>{t('messages.leaseEnded')}</p>
      )}
    </div>
  );
}

function StatusSelect({ thread }: { thread: Thread }) {
  const { t } = useTranslation();
  const setStatus = useSetFaultStatus(thread.id);
  return (
    <label className={s.statusSelect}>
      <span className="visually-hidden">{t('messages.status')}</span>
      <select
        value={thread.status ?? 'new'}
        disabled={setStatus.isPending}
        onChange={(e) => setStatus.mutate(e.target.value as FaultStatus)}
        className={`${s.chip} ${s[`chip_${thread.status}`]}`}
      >
        {faultStatuses.map((st) => (
          <option key={st} value={st}>
            {t(`faultStatus.${st}`)}
          </option>
        ))}
      </select>
    </label>
  );
}

function Composer({ threadId }: { threadId: string }) {
  const { t } = useTranslation();
  const send = useSendMessage(threadId);
  const [text, setText] = useState('');

  function submit(event?: FormEvent) {
    event?.preventDefault();
    const body = text.trim();
    if (!body || send.isPending) return;
    send.mutate(body, { onSuccess: () => setText('') });
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    // Ctrl/Cmd + Enter lähettää; pelkkä Enter tekee rivinvaihdon.
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) submit();
  }

  return (
    <form className={s.composer} onSubmit={submit}>
      <label className="visually-hidden" htmlFor={`reply-${threadId}`}>
        {t('messages.reply')}
      </label>
      <textarea
        id={`reply-${threadId}`}
        className={s.composerInput}
        rows={2}
        value={text}
        placeholder={t('messages.replyPlaceholder')}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        maxLength={4000}
      />
      <Button type="submit" variant="primary" disabled={!text.trim() || send.isPending}>
        <Send size={16} aria-hidden="true" />
        {t('messages.send')}
      </Button>
      {send.isError && <Alert>{t(errorKey(send.error))}</Alert>}
    </form>
  );
}

/**
 * Uusi ketju. Vuokranantajan kaikista viesteistä valitaan ensin asunto; vuokralaiselle ja
 * asunnon välilehdellä asunto on jo tiedossa. Vuokralainen voi valita myös vikailmoituksen.
 */
export function NewThreadDialog({
  as,
  propertyId,
  kind: fixedKind,
  onClose,
  onCreated,
}: {
  as: MessagesAs;
  propertyId?: string;
  kind?: ThreadKind;
  onClose: () => void;
  onCreated: (thread: Thread) => void;
}) {
  const { t } = useTranslation();
  const create = useCreateThread();
  const { data: owned } = useProperties('owner');
  const rented = (owned ?? []).filter((p) => p.currentLease && !p.archivedAt);
  const [chosen, setChosen] = useState(propertyId ?? '');
  const selectedProperty = propertyId ?? chosen;
  const { data: leases, isLoading: leasesLoading } = useLeases(selectedProperty || undefined);
  const lease = activeLease(leases);
  const [kind, setKind] = useState<ThreadKind>(fixedKind ?? 'message');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!propertyId && !chosen && rented.length === 1) setChosen(rented[0]!.id);
  }, [propertyId, chosen, rented]);

  function submit(event: FormEvent) {
    event.preventDefault();
    const parsed = threadCreateSchema.safeParse({ kind, subject, body });
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) next[String(issue.path[0])] ??= t(issue.message);
      setErrors(next);
      return;
    }
    if (!lease) return;
    create.mutate({ leaseId: lease.id, body: parsed.data }, { onSuccess: onCreated });
  }

  const isFault = kind === 'fault';
  const noLease = Boolean(selectedProperty) && !leasesLoading && !lease;
  // Viestille ei ole vastaanottajaa, jos yksikään vuokralainen ei käytä Vuokranttia.
  const noAccount =
    as === 'owner' && Boolean(lease) && !lease!.parties.some((p) => p.access === 'joined');

  return (
    <Dialog
      open
      onClose={onClose}
      title={isFault ? t('messages.newFault') : t('messages.newTitle')}
    >
      <form onSubmit={submit} noValidate style={{ display: 'grid', gap: '1.1rem' }}>
        {isFault && <p style={{ color: 'var(--muted)' }}>{t('fault.lead')}</p>}
        {!propertyId && as === 'owner' && (
          <SelectField
            label={t('messages.property')}
            value={chosen}
            onChange={(e) => setChosen(e.target.value)}
          >
            <option value="" disabled>
              {t('messages.chooseProperty')}
            </option>
            {rented.map((p) => (
              <option key={p.id} value={p.id}>
                {propertyTitle(p)}
              </option>
            ))}
          </SelectField>
        )}
        {as === 'party' && !fixedKind && (
          <SelectField
            label={t('messages.kind')}
            value={kind}
            onChange={(e) => setKind(e.target.value as ThreadKind)}
          >
            <option value="message">{t('messages.kindMessage')}</option>
            <option value="fault">{t('messages.kindFault')}</option>
          </SelectField>
        )}
        <TextField
          label={isFault ? t('fault.subject') : t('messages.subject')}
          hint={isFault ? t('fault.subjectHint') : undefined}
          value={subject}
          maxLength={200}
          onChange={(e) => setSubject(e.target.value)}
          error={errors.subject}
        />
        <div>
          <TextArea
            label={isFault ? t('fault.what') : t('messages.body')}
            hint={isFault ? t('fault.whatHint') : undefined}
            rows={5}
            value={body}
            maxLength={4000}
            onChange={(e) => setBody(e.target.value)}
          />
          {errors.body && <Alert>{errors.body}</Alert>}
        </div>
        <p style={{ color: 'var(--muted)', fontSize: 'var(--text-sm)' }}>
          {as === 'owner' ? t('messages.visibleToTenants') : t('messages.visibleToLandlord')}
        </p>
        {noLease && <Alert>{t('messages.noLease')}</Alert>}
        {noAccount && <Alert>{t('messages.noAccount')}</Alert>}
        {create.isError && <Alert>{t(errorKey(create.error))}</Alert>}
        <DialogActions>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!lease || noAccount || create.isPending}
          >
            {isFault ? t('fault.send') : t('messages.send')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
